/**
 * LeagueLeaderboard.jsx
 * -----------------------------------------------------------------------------
 * Live leaderboard component for all three league tiers.
 * Shows position, player, points, zone badge, and projected prize.
 *
 * Props:
 *   leagueId    — 'black_caviar' | 'so_you_think' | 'lonhro'
 *   seasonId    — optional, defaults to active season
 *   currentUserId — highlight the logged-in player's row
 * -----------------------------------------------------------------------------
 */
import { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getPublicName, getProfileImageUrl, DEFAULT_PROFILE_IMAGE } from '../utils/profileImage';
import { hydratePublicProfiles } from '../services/publicProfiles';

// ── Zone styling ──────────────────────────────────────────────────────────────
const ZONE_STYLES = {
  promotion: {
    bg:     'rgba(34, 197, 94, 0.12)',
    border: '#22c55e',
    badge:  { bg: '#22c55e', text: '#fff', label: '▲ PROMOTION' },
  },
  safe: {
    bg:     'transparent',
    border: 'transparent',
    badge:  null,
  },
  danger: {
    bg:     'rgba(234, 179, 8, 0.10)',
    border: '#eab308',
    badge:  { bg: '#eab308', text: '#000', label: '⚠ DANGER' },
  },
  relegation: {
    bg:     'rgba(239, 68, 68, 0.10)',
    border: '#ef4444',
    badge:  { bg: '#ef4444', text: '#fff', label: '▼ RELEGATION' },
  },
};

const LEAGUE_META = {
  black_caviar: { name: 'Black Caviar League', emoji: '🏆', colour: '#C9A84C' },
  so_you_think: { name: 'So You Think League', emoji: '🥈', colour: '#7B8FA1' },
  lonhro:       { name: 'Lonhro League',        emoji: '🥉', colour: '#A0522D' },
};

// ── Sub-components ────────────────────────────────────────────────────────────

function ZoneBadge({ zone }) {
  const style = ZONE_STYLES[zone];
  if (!style?.badge) return null;
  return (
    <span style={{
      display:       'inline-block',
      padding:       '2px 8px',
      borderRadius:  '4px',
      fontSize:      '10px',
      fontWeight:    '700',
      letterSpacing: '0.05em',
      backgroundColor: style.badge.bg,
      color:           style.badge.text,
      marginLeft:      '8px',
    }}>
      {style.badge.label}
    </span>
  );
}

function PositionBadge({ position }) {
  const medals = { 1: '🥇', 2: '🥈', 3: '🥉' };
  return (
    <span style={{ fontWeight: '700', fontSize: '14px', color: position <= 3 ? '#C9A84C' : '#9CA3AF' }}>
      {medals[position] || `#${position}`}
    </span>
  );
}

function PointsBar({ points, maxPoints }) {
  const pct = maxPoints > 0 ? Math.round((points / maxPoints) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ fontWeight: '600', color: '#F8F9FA', minWidth: '60px', textAlign: 'right' }}>
        {points.toLocaleString()}
      </span>
      <div style={{ flex: 1, height: '6px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '3px', minWidth: '80px' }}>
        <div style={{
          width:           `${pct}%`,
          height:          '100%',
          borderRadius:    '3px',
          backgroundColor: '#3B82F6',
          transition:      'width 0.3s ease',
        }} />
      </div>
    </div>
  );
}

// ── League blurbs ─────────────────────────────────────────────────────────────

const LEAGUE_BLURBS = {
  lonhro: {
    title:     'Welcome to the Lonhro League',
    colour:    '#A0522D',
    highlight: 'Top performers earn promotion to So You Think League each season.',
    body: [
      "The Lonhro League is where every Syndicator Showdown journey begins. Named after one of Australia's greatest racehorses — a champion who won 26 races including 10 Group 1s — this league is your proving ground.",
      "There is no cap on numbers here. Every player who joins Syndicator Showdown enters the Lonhro League and competes across our weekly showdowns to accumulate season points. Finish in the top positions at season end and you earn promotion to the So You Think League — with bigger prize pools and tougher competition waiting.",
      "As our player base grows, the So You Think League and Black Caviar League will open, unlocking larger prizes and more exclusive competition. The path starts here.",
    ],
  },
  so_you_think: {
    title:     'So You Think League — Earned, Not Given',
    colour:    '#7B8FA1',
    highlight: 'Capped places. Earned entry. Bigger prizes. Real stakes.',
    body: [
      "Named after Australia's brilliant champion So You Think — winner of 14 Group 1 races across two continents — this league is reserved for players who have proven themselves in the Lonhro League.",
      "Places here are capped. You cannot simply join the So You Think League — you earn your way in through performance and promotion. With a larger prize pool than Lonhro and a more competitive field, every showdown matters more.",
      "Finish in the top positions at season end and you earn the right to compete in the Black Caviar League — the most exclusive tier on the platform. Finish in the bottom positions and you return to Lonhro to fight your way back. There is no comfort zone here.",
    ],
  },
  black_caviar: {
    title:     'Black Caviar League — The Pinnacle',
    colour:    '#C9A84C',
    highlight: '500 places only. The most exclusive fantasy racing competition in Australia.',
    body: [
      "Named after the undefeated Black Caviar — 25 wins from 25 starts, the greatest sprinter Australia has ever produced — this league represents the absolute pinnacle of Syndicator Showdown competition.",
      "Only 500 players will ever hold a place in the Black Caviar League at any one time. Entry is by promotion only — the top performers from the So You Think League and the very best from Lonhro earn their place here each season. Everyone else watches from below.",
      "The prize pool at this level reflects the exclusivity. Compete against the best 500 players on the platform for the largest prizes on offer. Survive and you stay. Falter and you fall — demotion is real and places are always under threat from the players below who are hungry to take yours.",
    ],
  },
};

// ── Main component ────────────────────────────────────────────────────────────

export default function LeagueLeaderboard({ leagueId = 'lonhro', seasonId, currentUserId }) {
  const [standings,     setStandings]     = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [page,          setPage]          = useState(1);
  const [leagueMeta,    setLeagueMeta]    = useState(null);
  const [leagueEnabled, setLeagueEnabled] = useState(true);
  const [activeLeague,  setActiveLeague]  = useState(leagueId);
  const [profilesByUid, setProfilesByUid] = useState({});

  const PAGE_SIZE = 50;

  const fetchLeaderboard = useCallback(async (lid, p) => {
    setLoading(true);
    setError(null);
    try {
      const fn     = httpsCallable(getFunctions(undefined, 'australia-southeast1'), 'getLeaderboard');
      const result = await fn({ leagueId: lid, seasonId, page: p, pageSize: PAGE_SIZE });
      setStandings(result.data.standings || []);
      setLeagueMeta(result.data.league   || LEAGUE_META[lid]);
      setLeagueEnabled(result.data.enabled ?? true);
    } catch (err) {
      console.error('[LeagueLeaderboard] fetch error:', err);
      setError('Unable to load leaderboard. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [seasonId]);

  useEffect(() => {
    fetchLeaderboard(activeLeague, page);
  }, [activeLeague, page, fetchLeaderboard]);

  // Public identity hydration — getLeaderboard() only returns userId; the
  // public Stable Name + avatar are hydrated client-side from the public
  // player_profiles/{uid} mirror (never the private users/{uid} doc).
  useEffect(() => {
    const missing = [...new Set(standings.map(s => s.userId))]
      .filter(uid => uid && !(uid in profilesByUid));
    if (missing.length === 0) return;
    let cancelled = false;
    hydratePublicProfiles(missing).then(map => {
      if (cancelled) return;
      setProfilesByUid(prev => ({ ...prev, ...map }));
    });
    return () => { cancelled = true; };
  }, [standings, profilesByUid]);

  const maxPoints = standings.length > 0 ? standings[0].totalPoints : 1;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
    <div style={{
      backgroundColor: '#0A0E1A',
      borderRadius:    '12px',
      border:          '1px solid rgba(255,255,255,0.08)',
      marginTop:       '80px',
    }}>

      {/* League tabs */}
      <div style={{
        display:         'flex',
        gap:             '4px',
        padding:         '16px 16px 0',
        backgroundColor: '#1A2540',
        borderRadius:    '12px 12px 0 0',
        borderBottom:    '2px solid rgba(255,255,255,0.08)',
      }}>
        {[
          { id: 'black_caviar', emoji: '🏆', name: 'Black Caviar League', colour: '#C9A84C' },
          { id: 'so_you_think', emoji: '🥈', name: 'So You Think League', colour: '#7B8FA1' },
          { id: 'lonhro',       emoji: '🥉', name: 'Lonhro League',        colour: '#A0522D' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveLeague(tab.id); setPage(1); }}
            style={{
              padding:         '10px 20px',
              borderRadius:    '8px 8px 0 0',
              border:          activeLeague === tab.id
                                 ? `2px solid ${tab.colour}`
                                 : '2px solid rgba(255,255,255,0.15)',
              borderBottom:    activeLeague === tab.id
                                 ? '2px solid transparent'
                                 : '2px solid rgba(255,255,255,0.08)',
              backgroundColor: activeLeague === tab.id
                                 ? `${tab.colour}33`
                                 : 'rgba(255,255,255,0.05)',
              color:           activeLeague === tab.id ? tab.colour : '#9CA3AF',
              fontWeight:      activeLeague === tab.id ? '700' : '400',
              fontSize:        '13px',
              cursor:          'pointer',
              whiteSpace:      'nowrap',
            }}
          >
            {tab.emoji} {tab.name}
          </button>
        ))}
      </div>

      {/* Header */}
      <div style={{ padding: '20px 24px 12px', display: 'flex',
                    justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0, color: leagueMeta?.colour || '#F8F9FA',
                       fontSize: '20px', fontWeight: '700' }}>
            {leagueMeta?.emoji} {leagueMeta?.name || LEAGUE_META[activeLeague].name}
          </h2>
          <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: '13px' }}>
            Season standings · Page {page} · {standings.length} players shown
          </p>
        </div>
        <button
          onClick={() => fetchLeaderboard(activeLeague, page)}
          style={{ padding: '8px 16px', backgroundColor: 'rgba(59,130,246,0.15)',
                   border: '1px solid #3B82F6', borderRadius: '6px',
                   color: '#3B82F6', fontSize: '12px', cursor: 'pointer' }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* Zone legend */}
      <div style={{ padding: '0 24px 12px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        {Object.entries(ZONE_STYLES).filter(([, v]) => v.badge).map(([zone, style]) => (
          <span key={zone} style={{ display: 'flex', alignItems: 'center',
                                    gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <span style={{ width: '10px', height: '10px', borderRadius: '2px',
                           backgroundColor: style.badge.bg, display: 'inline-block' }} />
            {style.badge.label}
          </span>
        ))}
      </div>

      {/* Locked / disabled league message */}
      {!leagueEnabled ? (
        <div style={{ padding: '80px 40px', textAlign: 'center' }}>
          <div style={{ fontSize: '32px', marginBottom: '16px' }}>🔒</div>
          <p style={{ color: '#F8F9FA', fontSize: '20px', fontWeight: '700', margin: '0 0 8px' }}>
            League Currently Closed
          </p>
          <p style={{ color: '#9CA3AF', fontSize: '14px', margin: 0 }}>
            This league will open when player milestones are reached. Stay tuned for announcements.
          </p>
        </div>
      ) : (
      <>

      {/* Table header */}
      <div style={{
        display:             'grid',
        gridTemplateColumns: '60px 1fr 180px 120px 100px',
        padding:             '8px 24px',
        backgroundColor:     'rgba(255,255,255,0.04)',
        borderTop:           '1px solid rgba(255,255,255,0.06)',
        borderBottom:        '1px solid rgba(255,255,255,0.06)',
      }}>
        {['POS', 'PLAYER', 'POINTS', 'SHOWDOWNS', 'PRIZE'].map(h => (
          <span key={h} style={{ fontSize: '10px', fontWeight: '700',
                                 color: '#6B7280', letterSpacing: '0.08em' }}>{h}</span>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ padding: '60px', textAlign: 'center', color: '#6B7280' }}>
          Loading standings...
        </div>
      ) : error ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#EF4444' }}>{error}</div>
      ) : standings.length === 0 ? (
        <div style={{ padding: '60px', textAlign: 'center', color: '#6B7280' }}>
          No standings yet for this season.
        </div>
      ) : (
        <div>
          {standings.map((s) => {
            const zoneStyle = ZONE_STYLES[s.zone] || ZONE_STYLES.safe;
            const isMe      = s.userId === currentUserId;
            const league    = LEAGUE_META[activeLeague];
            return (
              <div
                key={s.userId}
                style={{
                  display:             'grid',
                  gridTemplateColumns: '60px 1fr 180px 120px 100px',
                  padding:             '12px 24px',
                  backgroundColor:     isMe ? `${league.colour}18` : zoneStyle.bg,
                  borderLeft:          isMe
                    ? `3px solid ${league.colour}`
                    : `3px solid ${zoneStyle.border}`,
                  borderBottom:        '1px solid rgba(255,255,255,0.04)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <PositionBadge position={s.position} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <img
                    src={getProfileImageUrl(profilesByUid[s.userId], null)}
                    alt=""
                    onError={e => { e.currentTarget.src = DEFAULT_PROFILE_IMAGE; }}
                    style={{
                      width: '28px', height: '28px', borderRadius: '50%',
                      objectFit: 'cover', flexShrink: 0,
                      border: `1.5px solid ${league.colour}88`,
                    }}
                  />
                  <div>
                    <div style={{ color: isMe ? league.colour : '#F8F9FA',
                                  fontWeight: isMe ? '700' : '400', fontSize: '13px' }}>
                      {getPublicName(profilesByUid[s.userId], null)}
                      {isMe && <span style={{ marginLeft: '6px', fontSize: '10px',
                                             color: league.colour }}>(You)</span>}
                    </div>
                    <ZoneBadge zone={s.zone} />
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <PointsBar points={s.totalPoints} maxPoints={maxPoints} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center',
                              color: '#9CA3AF', fontSize: '13px' }}>
                  {s.showdownsPlayed}
                  {s.showdownsPlayed < 20 && (
                    <span style={{ marginLeft: '6px', fontSize: '10px', color: '#EAB308' }}
                          title="Needs 20 showdowns for eligibility">⚠</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center',
                              color: s.position <= 10 ? '#22C55E' : '#6B7280',
                              fontSize: '13px',
                              fontWeight: s.position <= 10 ? '600' : '400' }}>
                  {s.position <= 10 ? '💰' : '—'}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      <div style={{ padding: '16px 24px', display: 'flex', justifyContent: 'center',
                    gap: '8px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <button
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1}
          style={{ padding: '6px 16px',
                   backgroundColor: page === 1 ? 'rgba(255,255,255,0.04)' : 'rgba(59,130,246,0.15)',
                   border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px',
                   color: page === 1 ? '#6B7280' : '#3B82F6',
                   cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: '13px' }}
        >
          ← Prev
        </button>
        <span style={{ padding: '6px 16px', color: '#9CA3AF', fontSize: '13px' }}>
          Page {page}
        </span>
        <button
          onClick={() => setPage(p => p + 1)}
          disabled={standings.length < PAGE_SIZE}
          style={{ padding: '6px 16px',
                   backgroundColor: standings.length < PAGE_SIZE ? 'rgba(255,255,255,0.04)' : 'rgba(59,130,246,0.15)',
                   border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px',
                   color: standings.length < PAGE_SIZE ? '#6B7280' : '#3B82F6',
                   cursor: standings.length < PAGE_SIZE ? 'not-allowed' : 'pointer',
                   fontSize: '13px' }}
        >
          Next →
        </button>
      </div>

      </>
      )} {/* end leagueEnabled check */}
    </div>

    {/* ── League blurb ── */}
    {(() => {
      const blurb = LEAGUE_BLURBS[activeLeague];
      if (!blurb) return null;
      return (
        <div style={{
          marginTop:       '32px',
          padding:         '32px',
          backgroundColor: 'rgba(255,255,255,0.03)',
          borderRadius:    '12px',
          border:          `1px solid ${blurb.colour}33`,
          borderLeft:      `4px solid ${blurb.colour}`,
        }}>
          <h3 style={{ margin: '0 0 16px', color: blurb.colour, fontSize: '18px', fontWeight: '700' }}>
            {blurb.title}
          </h3>
          {blurb.body.map((para, i) => (
            <p key={i} style={{ color: '#D1D5DB', fontSize: '14px', lineHeight: '1.7', margin: '0 0 12px' }}>
              {para}
            </p>
          ))}
          <div style={{
            marginTop:       '20px',
            padding:         '12px 16px',
            backgroundColor: `${blurb.colour}18`,
            borderRadius:    '8px',
            color:           blurb.colour,
            fontSize:        '13px',
            fontWeight:      '600',
          }}>
            ★ {blurb.highlight}
          </div>
        </div>
      );
    })()}
    </>
  );
}
